import styles from './SidebarItem.scss';

import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import debounce from 'lodash/debounce';
import once from 'lodash/once';
import React from 'react';

import { ContentType, HasImage, HasVideo, InternalLink, LinkSet, NavigationItem, Page, as, is } from 'src/api/contentful/contentTypes.cloverdotcom';
import { NavTitle } from 'src/components/NavTitle/NavTitle';
import { getField } from 'src/utils/EntryUtil';
import { VerticalLinkSet } from 'src/utils/NavigationUtil';

export interface ISidebarItem extends React.ButtonHTMLAttributes<HTMLElement> {
  open?: boolean;
  active?: boolean;
  parentEntry?: ReferencedEntry<LinkSet & VerticalLinkSet>;
  entry?: ReferencedEntry<NavigationItem | VerticalLinkSet> ;
  onOpen?: (submenu: ReferencedEntry<LinkSet & VerticalLinkSet>) => void;
  onActivate?: (backgroundColor?: string, mediaOverride?: ReferencedEntry<HasVideo | HasImage>) => void;
  children?: never;
  titleTag?: string;
}
export class SidebarItem extends React.PureComponent<ISidebarItem> {

  readonly el = React.createRef<HTMLButtonElement | HTMLAnchorElement>();

  get page() {
    // Try and get the passed in Page if the entry is a Page
    // Otherwise try and get the Page from the InternalLink if the entry is an InternalLink
    return (
      as<Page>(ContentType.BASIC_PAGE, this.props.entry) ||
      as<Page>(ContentType.BASIC_PAGE, getField(as<InternalLink>(ContentType.INTERNAL_LINK, this.props.entry), 'page'))
    );
  }

  get verticalLinkSet() {
    // Try and get the VerticalLinkSet if the passed in entry is a VerticalLinkSet
   return as<VerticalLinkSet>(ContentType.LINK_SET, this.props.entry);
  }

  get verticalMarket() {
    // Prefer the VerticalMarket from the VerticalLinkSet
    // Otherwise try and get the VerticalMarket from the Page
    return (
      getField(this.verticalLinkSet, 'verticalMarket') ||
      getField(this.page, 'verticalMarket')
    );
  }

  get heroTopic() {
    // Try and get the Topic from the Page
    return getField(this.page, 'heroTopic');
  }

  get backgroundColor() {
    // Try and get the backgroundColor from the VerticalMarket;
    return getField(this.verticalMarket, 'primaryColor');
  }

  get parentVerticalMarket() {
    // Try and get the parentVerticalMarket from the parentEntry if the parentEntry is a VerticalLinkSet
    return getField(as<VerticalLinkSet>(ContentType.LINK_SET, this.props.parentEntry), 'verticalMarket');
  }

  get mediaOverride() {
    // cache the verticalMarket getter
    const verticalMarket = this.verticalMarket;

    // cache the parentVerticalMarket getter
    const parentVerticalMarket = this.parentVerticalMarket;

    // If the parentVerticalMarket exists and equals the verticalMarket
    // Prefer the heroTopic over the verticalMarket for the mediaOverride
    // else Prefer the verticalMarket over the heroTopic
    return parentVerticalMarket && parentVerticalMarket === verticalMarket
      ? this.heroTopic || verticalMarket
      : verticalMarket || this.heroTopic;
  }

  readonly activateOnce = once(() => this.activate());

  readonly activate = debounce(() => {
    if (!this.props.open || this.props.active) return;

    if (this.props.onActivate) this.props.onActivate(this.backgroundColor, this.mediaOverride);
  });

  readonly open = debounce(() => {
    if (!this.props.active) return;

    const linkSet = as<VerticalLinkSet>(ContentType.LINK_SET, this.props.entry);

    if (this.props.onOpen && linkSet) this.props.onOpen(linkSet);
  });

  componentDidMount() {
    if (this.props.active && this.el.current) this.el.current.focus();
  }

  componentDidUpdate(prevProps: Readonly<ISidebarItem>) {
    this.toggleFocus(!!prevProps.active);
  }

  render() {
    const {
      onOpen,
      onActivate,
      className,
      active,
      entry,
      parentEntry,
      ...props
    } = this.props;

    if (!entry || !entry.fields) return null;

    return (
      <NavTitle
        {...props}
        entry={entry}
        ref={this.el}
        className={cx(className, styles.sidebarItem, {
          [styles.sidebarItem]: active,
        })}
        onMouseEnter={this.activate}
        // when switching to a submenu the mouse might be over the element already
        onMouseOver={this.activateOnce}
        onMouseLeave={() => this.el.current && this.el.current.blur()}
        onFocus={this.activate}
        onClick={is<VerticalLinkSet>(ContentType.LINK_SET, entry) ? this.open : undefined}
      />
    );
  }

  private toggleFocus(prevActive: boolean) {
    if (!this.el.current) return;

    // from inactive to active
    if (!prevActive && this.props.active) this.el.current.focus();

    // from active to inactive
    if (prevActive && !this.props.active) this.el.current.blur();
  }
}
